<template>
    <div class="flex flex-col items-center justify-center gap-4">
        <BlendedCircles class="max-w-120" />
        <p class="display-serif">{{ mainLoadingMessage ?? t("components.profileWizard.loading.building") }}</p>
        <Transition v-if="!hideMessages" name="slide" mode="out-in">
            <p :key="currentMessageShown" class="text-ink-muted scaffold-body">{{ currentMessageShown }}</p>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { useIntervalFn } from "@vueuse/core";
import { computed, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import BlendedCircles from "../BlendedCircles.vue";

const { t } = useI18n();

type LoadingStepProps = {
    mainLoadingMessage?: string;
    hasPerspective?: boolean;
    hideMessages?: boolean;
};

const { mainLoadingMessage, hasPerspective = false, hideMessages = false } = defineProps<LoadingStepProps>();

const messages = computed(() => {
    const msgArr = [t("components.profileWizard.loading.analyzing"), t("components.profileWizard.loading.tailoring"), t("components.profileWizard.loading.crafting")];
    if (hasPerspective) {
        msgArr.push(t("components.profileWizard.loading.importing"));
    }
    return msgArr;
});
let messageIndex = 0;
const currentMessageShown = ref(messages.value[0]);

const { pause } = useIntervalFn(() => {
    messageIndex = (messageIndex + 1) % messages.value.length;
    currentMessageShown.value = messages.value[messageIndex]!;
}, 1500);

onUnmounted(() => {
    pause();
});
</script>

<style scoped>
.slide-leave-active,
.slide-enter-active {
    transition:
        opacity var(--motion-duration-slow) var(--motion-ease-enter),
        transform var(--motion-duration-slow) var(--motion-ease-enter);
}

.slide-leave-from,
.slide-enter-to {
    opacity: 1;
    transform: translateY(0);
}

.slide-enter-from {
    opacity: 0;
    transform: translateY(-0.5lh);
}
.slide-leave-to {
    opacity: 0;
    transform: translateY(0.5lh);
}

@media (prefers-reduced-motion: reduce) {
    .slide-leave-active,
    .slide-enter-active {
        transition-property: opacity;
    }

    .slide-enter-from,
    .slide-leave-to {
        transform: none;
    }
}
</style>
